import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { InferType } from 'yup';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';

import { EditProductProps } from './EditProduct';
import { schema } from './schema';

type EditProductFormData = InferType<typeof schema>;

const useEditProduct = ({ product, getProduct, onClose }: EditProductProps) => {
  const [loading, setLoading] = useState(false);
  const [newImage, setNewImage] = useState<File | null>(null);
  const http = initializeHttpProvider();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: product?.name,
      price: product?.price,
      value: product?.value,
    },
  });

  const onChangeImage = useCallback((file: File) => {
    setNewImage(file);
  }, []);

  const resetImage = useCallback(() => {
    setNewImage(null);
  }, []);

  const getImageUrl = async (image: FormData) => {
    try {
      const response = await http.inventory.uploadImage({ images: image, sku: product.sku });
      return response;
    } catch {
      toast.error('Error al cargar imagen');
    }
  };

  const onSubmit = async (data: EditProductFormData) => {
    setLoading(true);
    try {
      let thumbnailUrl = undefined;

      if (newImage) {
        const formData = new FormData();
        formData.append('images', newImage);
        const imageResponse = await getImageUrl(formData);
        thumbnailUrl = imageResponse?.data?.thumbnail;
      }

      await http.inventory.updateProduct({
        ...data,
        sku: product.sku,
        ...(thumbnailUrl && { thumbnail: thumbnailUrl }),
      });

      toast.success('Producto actualizado correctamente.');
      onClose();
      getProduct();
    } catch {
      toast.error('Hubo un error al modificar el producto.');
    } finally {
      setLoading(false);
    }
  };

  return {
    onChangeImage,
    resetImage,
    register,
    errors,
    handleSubmit,
    setValue,
    getValues,
    onSubmit,
    isDirty: isDirty || !!newImage, // El formulario está "dirty" si hay cambios en el form o hay una nueva imagen
    loading,
    newImage,
  };
};

export default useEditProduct;
